import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState, useRef } from "react";
const glasswareModules = import.meta.glob("../domains/chemistry/parts/glassware/*.json", { eager: true });
const speciesModules = import.meta.glob("../domains/chemistry/data/species.json", { eager: true });
const GLASSWARE_PART_IDS = Object.values(glasswareModules)
    .map((m) => m.default?.id)
    .filter((id) => typeof id === "string")
    .sort();
const SPECIES_FORMULAS = Object.values(speciesModules)
    .flatMap((m) => Object.keys(m.default?.species ?? {}))
    .sort();
const CONDITION_KINDS = [
    "ph-below",
    "ph-above",
    "volume-above-ml",
    "species-conc-above",
    "temperature-above-k",
    "indicator-cleared"
];
const CONDITION_LABEL = {
    "ph-below": "ph اقل من",
    "ph-above": "ph اكبر من",
    "volume-above-ml": "حجم اكبر من (مل)",
    "volume-below-ml": "حجم اقل من (مل)",
    "species-conc-above": "تركيز نوع اكبر من",
    "species-conc-below": "تركيز نوع اقل من",
    "temperature-above-k": "حرارة اكبر من (k)",
    "temperature-below-k": "حرارة اقل من (k)",
    "indicator-cleared": "اختفاء الكاشف",
    "after-seconds": "بعد ثوان"
};
function makeUid() {
    return Math.random().toString(36).slice(2, 10);
}
function emptyInstance() {
    return {
        uid: makeUid(),
        id: "",
        part: GLASSWARE_PART_IDS[0] ?? "",
        x: "0",
        y: "1",
        z: "0"
    };
}
function emptyStep() {
    return {
        uid: makeUid(),
        id: "",
        prompt: "",
        success: "",
        conditionKind: "none",
        target: "",
        formula: SPECIES_FORMULAS[0] ?? "",
        threshold: "0",
        highlight: ""
    };
}
function buildCondition(s) {
    if (s.conditionKind === "none")
        return null;
    const v = Number(s.threshold);
    switch (s.conditionKind) {
        case "indicator-cleared":
            return { kind: "indicator-cleared", target: s.target };
        case "species-conc-above":
            return { kind: "species-conc-above", target: s.target, formula: s.formula, value: v };
        case "species-conc-below":
            return { kind: "species-conc-below", target: s.target, formula: s.formula, value: v };
        case "after-seconds":
            return { kind: "after-seconds", value: v };
        case "ph-below":
        case "ph-above":
        case "volume-above-ml":
        case "volume-below-ml":
        case "temperature-above-k":
        case "temperature-below-k":
            return { kind: s.conditionKind, target: s.target, value: v };
    }
}
function buildScenarioJSON(meta, instances, steps) {
    const instJSON = instances.map((i) => ({
        id: i.id,
        part: i.part,
        position: [Number(i.x), Number(i.y), Number(i.z)]
    }));
    const stepJSON = steps.map((s, idx) => {
        const id = s.id || `step-${idx + 1}`;
        const cond = buildCondition(s);
        const out = { id, prompt: s.prompt };
        if (cond)
            out.condition = cond;
        if (s.success.trim())
            out.success = s.success;
        if (s.highlight.trim())
            out.highlight = s.highlight;
        return out;
    });
    const out = {
        id: meta.id,
        title: meta.title,
        summary: meta.summary,
        instances: instJSON,
        steps: stepJSON
    };
    const lvl = Number(meta.level);
    if (lvl >= 1 && lvl <= 5) {
        out.level = lvl;
    }
    return out;
}
function validate(json) {
    const issues = [];
    if (!json.id.trim())
        issues.push("لا يوجد معرف للسيناريو (id).");
    if (!/^[a-z0-9/_-]+$/.test(json.id)) {
        issues.push("معرف السيناريو يجب ان يكون slug (احرف انجليزية صغيرة وارقام و / _ -).");
    }
    if (!json.title.trim())
        issues.push("لا يوجد عنوان (title).");
    if (!json.summary.trim())
        issues.push("لا يوجد ملخص (summary).");
    if (json.instances.length === 0)
        issues.push("لا توجد ادوات على الطاولة.");
    const ids = new Set();
    for (const inst of json.instances) {
        if (!inst.id.trim())
            issues.push(`اداة بدون معرف (part=${inst.part}).`);
        if (ids.has(inst.id))
            issues.push(`معرف مكرر: ${inst.id}.`);
        ids.add(inst.id);
        if (!GLASSWARE_PART_IDS.includes(inst.part)) {
            issues.push(`اداة غير معروفة: ${inst.part} (instance ${inst.id}).`);
        }
        if (inst.position.some((n) => Number.isNaN(n))) {
            issues.push(`موقع غير صالح للاداة ${inst.id}.`);
        }
    }
    json.steps.forEach((step, idx) => {
        const tag = `الخطوة ${idx + 1}`;
        if (!step.prompt.trim())
            issues.push(`${tag}: لا يوجد نص للسؤال.`);
        if (step.condition) {
            const c = step.condition;
            // `after-seconds` is time-based and has no `target`; skip the
            // binding-id check for it (session 348).
            if (c.kind !== "after-seconds") {
                if (!ids.has(c.target)) {
                    issues.push(`${tag}: الهدف "${c.target}" لا يطابق اي اداة على الطاولة.`);
                }
            }
            if ("formula" in c) {
                if (!SPECIES_FORMULAS.includes(c.formula)) {
                    issues.push(`${tag}: صيغة غير معروفة "${c.formula}".`);
                }
            }
            if ("value" in c && Number.isNaN(c.value)) {
                issues.push(`${tag}: قيمة العتبة غير رقمية.`);
            }
        }
    });
    return issues;
}
/**
 * Phase C scenario builder (session 337). A teacher composes a
 * guided experiment using forms — meta, bench instances, steps,
 * conditions — then exports the resulting JSON as a download. No
 * file is written into the source tree from this view; the JSON
 * has to be checked in manually after editorial review.
 */
export function ScenarioBuilder({ onExit }) {
    const [scenarioId, setScenarioId] = useState("chemistry/my-lesson");
    const [title, setTitle] = useState("");
    const [summary, setSummary] = useState("");
    const [level, setLevel] = useState("1");
    const [instances, setInstances] = useState([emptyInstance()]);
    const [steps, setSteps] = useState([emptyStep()]);
    const [issues, setIssues] = useState(null);
    const fileInputRef = useRef(null);
    const json = useMemo(() => buildScenarioJSON({ id: scenarioId, title, summary, level }, instances, steps), [scenarioId, title, summary, level, instances, steps]);
    const jsonText = useMemo(() => JSON.stringify(json, null, 2), [json]);
    const addInstance = () => {
        if (instances.length >= 12)
            return;
        setInstances((cur) => [...cur, emptyInstance()]);
    };
    const removeInstance = (uid) => {
        setInstances((cur) => cur.filter((i) => i.uid !== uid));
    };
    const patchInstance = (uid, patch) => {
        setInstances((cur) => cur.map((i) => (i.uid === uid ? { ...i, ...patch } : i)));
    };
    const addStep = () => setSteps((cur) => [...cur, emptyStep()]);
    const removeStep = (uid) => {
        setSteps((cur) => cur.filter((s) => s.uid !== uid));
    };
    const patchStep = (uid, patch) => {
        setSteps((cur) => cur.map((s) => (s.uid === uid ? { ...s, ...patch } : s)));
    };
    const moveStep = (uid, dir) => {
        setSteps((cur) => {
            const idx = cur.findIndex((s) => s.uid === uid);
            const next = idx + dir;
            if (idx < 0 || next < 0 || next >= cur.length)
                return cur;
            const out = cur.slice();
            const tmp = out[idx];
            out[idx] = out[next];
            out[next] = tmp;
            return out;
        });
    };
    const onValidate = () => setIssues(validate(json));
    const onExport = () => {
        const blob = new Blob([jsonText], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const slug = scenarioId.replace(/[^a-z0-9-]+/gi, "-") || "scenario";
        a.download = `${slug}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };
    const onImportClick = () => fileInputRef.current?.click();
    const onImport = (e) => {
        const file = e.target.files?.[0];
        if (!file)
            return;
        const reader = new FileReader();
        reader.onload = () => {
            try {
                const parsed = JSON.parse(String(reader.result));
                applyImported(parsed);
                setIssues(null);
            }
            catch (err) {
                setIssues([`فشل قراءة الملف: ${err instanceof Error ? err.message : String(err)}`]);
            }
        };
        reader.readAsText(file);
        e.target.value = "";
    };
    const applyImported = (parsed) => {
        setScenarioId(parsed.id ?? "");
        setTitle(parsed.title ?? "");
        setSummary(parsed.summary ?? "");
        setLevel(parsed.level !== undefined ? String(parsed.level) : "1");
        setInstances((parsed.instances ?? []).map((i) => ({
            uid: makeUid(),
            id: i.id,
            part: i.part,
            x: String(i.position?.[0] ?? 0),
            y: String(i.position?.[1] ?? 1),
            z: String(i.position?.[2] ?? 0)
        })));
        setSteps((parsed.steps ?? []).map((s) => {
            const c = s.condition ?? null;
            const out = {
                uid: makeUid(),
                id: s.id,
                prompt: s.prompt,
                success: s.success ?? "",
                conditionKind: c ? c.kind : "none",
                target: c && c.kind !== "after-seconds" ? c.target : "",
                formula: c && "formula" in c ? c.formula : (SPECIES_FORMULAS[0] ?? ""),
                threshold: c && "value" in c ? String(c.value) : "0",
                highlight: typeof s.highlight === "string" ? s.highlight : ""
            };
            return out;
        }));
    };
    const instanceIds = instances.map((i) => i.id).filter((s) => s.trim().length > 0);
    return (_jsxs("main", { className: "builder", children: [_jsxs("header", { className: "knaad-header", children: [_jsxs("div", { className: "knaad-header-left", children: [_jsx("button", { type: "button", className: "knaad-header-home", onClick: onExit, title: "\u0631\u062C\u0648\u0639", "aria-label": "\u0631\u062C\u0648\u0639", children: _jsx("span", { className: "lessons-mark" }) }), _jsxs("div", { className: "knaad-header-text", children: [_jsx("span", { className: "knaad-header-product", children: "\u0645\u0646\u062A\u062C \u0627\u0644\u0645\u0639\u0627\u0645\u0644 \u0627\u0644\u0627\u0641\u062A\u0631\u0627\u0636\u064A\u0629" }), _jsx("span", { className: "knaad-header-subtitle", children: "\u00B7 \u0628\u0646\u0627\u0621 \u062F\u0631\u0633" })] })] }), _jsx("div", { className: "knaad-header-right", children: _jsx("button", { type: "button", className: "knaad-header-back", onClick: onExit, children: "\u2190 \u0631\u062C\u0648\u0639" }) })] }), _jsxs("div", { className: "builder-shell", children: [_jsxs("section", { className: "builder-form", children: [_jsx("h2", { className: "builder-section-title", children: "\u0628\u064A\u0627\u0646\u0627\u062A \u0627\u0644\u062F\u0631\u0633" }), _jsxs("div", { className: "feedback-form", children: [_jsxs("label", { className: "feedback-field", children: [_jsx("span", { children: "\u0627\u0644\u0645\u0639\u0631\u0641 (slug)" }), _jsx("input", { type: "text", value: scenarioId, onChange: (e) => setScenarioId(e.target.value), placeholder: "chemistry/my-lesson" })] }), _jsxs("label", { className: "feedback-field", children: [_jsx("span", { children: "\u0627\u0644\u0639\u0646\u0648\u0627\u0646" }), _jsx("input", { type: "text", value: title, onChange: (e) => setTitle(e.target.value), placeholder: "\u0645\u062B\u0627\u0644: \u062A\u062E\u0641\u064A\u0641 \u0645\u062D\u0644\u0648\u0644 \u0645\u0644\u0648\u0646" })] }), _jsxs("label", { className: "feedback-field", children: [_jsx("span", { children: "\u0627\u0644\u0645\u0644\u062E\u0635" }), _jsx("textarea", { rows: 2, value: summary, onChange: (e) => setSummary(e.target.value), placeholder: "\u0633\u0637\u0631 \u0627\u0648 \u0633\u0637\u0631\u0627\u0646 \u064A\u0635\u0641\u0627\u0646 \u0627\u0644\u0647\u062F\u0641" })] }), _jsxs("label", { className: "feedback-field", children: [_jsx("span", { children: "\u0627\u0644\u0645\u0633\u062A\u0648\u0649 (1 - 5)" }), _jsxs("select", { value: level, onChange: (e) => setLevel(e.target.value), children: [_jsx("option", { value: "1", children: "1" }), _jsx("option", { value: "2", children: "2" }), _jsx("option", { value: "3", children: "3" }), _jsx("option", { value: "4", children: "4" }), _jsx("option", { value: "5", children: "5" })] })] })] }), _jsxs("div", { className: "builder-section-head", children: [_jsx("h2", { className: "builder-section-title", children: "\u0627\u062F\u0648\u0627\u062A \u0627\u0644\u0637\u0627\u0648\u0644\u0629" }), _jsx("button", { type: "button", className: "lessons-tool-btn", onClick: addInstance, disabled: instances.length >= 12, children: "+ \u0627\u0636\u0641 \u0627\u062F\u0627\u0629" })] }), instances.map((inst) => (_jsxs("div", { className: "builder-row", children: [_jsxs("label", { className: "feedback-field", children: [_jsx("span", { children: "\u0627\u0644\u0645\u0639\u0631\u0641 \u062F\u0627\u062E\u0644 \u0627\u0644\u062F\u0631\u0633" }), _jsx("input", { type: "text", value: inst.id, onChange: (e) => patchInstance(inst.uid, { id: e.target.value }), placeholder: "beaker-main" })] }), _jsxs("label", { className: "feedback-field", children: [_jsx("span", { children: "\u0627\u0644\u0642\u0637\u0639\u0629" }), _jsx("select", { value: inst.part, onChange: (e) => patchInstance(inst.uid, { part: e.target.value }), children: GLASSWARE_PART_IDS.map((pid) => (_jsx("option", { value: pid, children: pid }, pid))) })] }), _jsxs("label", { className: "feedback-field builder-coord", children: [_jsx("span", { children: "x" }), _jsx("input", { type: "number", step: "0.05", value: inst.x, onChange: (e) => patchInstance(inst.uid, { x: e.target.value }) })] }), _jsxs("label", { className: "feedback-field builder-coord", children: [_jsx("span", { children: "y" }), _jsx("input", { type: "number", step: "0.05", value: inst.y, onChange: (e) => patchInstance(inst.uid, { y: e.target.value }) })] }), _jsxs("label", { className: "feedback-field builder-coord", children: [_jsx("span", { children: "z" }), _jsx("input", { type: "number", step: "0.05", value: inst.z, onChange: (e) => patchInstance(inst.uid, { z: e.target.value }) })] }), _jsx("button", { type: "button", className: "lessons-tool-btn", onClick: () => removeInstance(inst.uid), title: "\u062D\u0630\u0641 \u0627\u0644\u0627\u062F\u0627\u0629", children: "\u062D\u0630\u0641" })] }, inst.uid))), _jsxs("div", { className: "builder-section-head", children: [_jsx("h2", { className: "builder-section-title", children: "\u0627\u0644\u062E\u0637\u0648\u0627\u062A" }), _jsx("button", { type: "button", className: "lessons-tool-btn", onClick: addStep, children: "+ \u0627\u0636\u0641 \u062E\u0637\u0648\u0629" })] }), steps.map((step, idx) => {
                                const needsFormula = step.conditionKind === "species-conc-above" ||
                                    step.conditionKind === "species-conc-below";
                                const needsValue = step.conditionKind !== "none" &&
                                    step.conditionKind !== "indicator-cleared";
                                return (_jsxs("div", { className: "builder-step", children: [_jsxs("div", { className: "builder-step-head", children: [_jsxs("span", { className: "builder-step-index", children: ["\u0627\u0644\u062E\u0637\u0648\u0629 ", idx + 1] }), _jsxs("div", { className: "builder-step-actions", children: [_jsx("button", { type: "button", className: "lessons-tool-btn", onClick: () => moveStep(step.uid, -1), disabled: idx === 0, title: "\u0644\u0644\u0627\u0639\u0644\u0649", children: "\u25B2" }), _jsx("button", { type: "button", className: "lessons-tool-btn", onClick: () => moveStep(step.uid, 1), disabled: idx === steps.length - 1, title: "\u0644\u0644\u0627\u0633\u0641\u0644", children: "\u25BC" }), _jsx("button", { type: "button", className: "lessons-tool-btn", onClick: () => removeStep(step.uid), title: "\u062D\u0630\u0641", children: "\u062D\u0630\u0641" })] })] }), _jsxs("label", { className: "feedback-field", children: [_jsx("span", { children: "\u0627\u0644\u0645\u0639\u0631\u0641 (\u0627\u062E\u062A\u064A\u0627\u0631\u064A)" }), _jsx("input", { type: "text", value: step.id, onChange: (e) => patchStep(step.uid, { id: e.target.value }), placeholder: `step-${idx + 1}` })] }), _jsxs("label", { className: "feedback-field", children: [_jsx("span", { children: "\u0627\u0644\u0633\u0624\u0627\u0644 / \u0627\u0644\u062A\u0639\u0644\u064A\u0645\u0629" }), _jsx("textarea", { rows: 2, value: step.prompt, onChange: (e) => patchStep(step.uid, { prompt: e.target.value }), placeholder: "\u0627\u0643\u062A\u0628 \u0627\u0644\u062A\u0639\u0644\u064A\u0645\u0629 \u0628\u0627\u0644\u0644\u063A\u0629 \u0627\u0644\u0639\u0631\u0628\u064A\u0629..." })] }), _jsxs("label", { className: "feedback-field", children: [_jsx("span", { children: "\u0631\u0633\u0627\u0644\u0629 \u0627\u0644\u0646\u062C\u0627\u062D" }), _jsx("textarea", { rows: 2, value: step.success, onChange: (e) => patchStep(step.uid, { success: e.target.value }), placeholder: "\u0639\u0628\u0627\u0631\u0629 \u062A\u0647\u0646\u0626\u0629 \u0639\u0646\u062F \u0627\u0644\u0627\u0646\u062A\u0647\u0627\u0621" })] }), _jsxs("div", { className: "builder-row", children: [_jsxs("label", { className: "feedback-field", children: [_jsx("span", { children: "\u0646\u0648\u0639 \u0627\u0644\u0634\u0631\u0637" }), _jsxs("select", { value: step.conditionKind, onChange: (e) => patchStep(step.uid, {
                                                                conditionKind: e.target.value
                                                            }), children: [_jsx("option", { value: "none", children: "\u0628\u062F\u0648\u0646 \u0634\u0631\u0637 (\u064A\u062F\u0648\u064A)" }), CONDITION_KINDS.map((k) => (_jsx("option", { value: k, children: CONDITION_LABEL[k] }, k)))] })] }), step.conditionKind !== "none" && (_jsxs("label", { className: "feedback-field", children: [_jsx("span", { children: "\u0627\u0644\u0647\u062F\u0641 (\u0627\u062F\u0627\u0629)" }), _jsxs("select", { value: step.target, onChange: (e) => patchStep(step.uid, { target: e.target.value }), children: [_jsx("option", { value: "", children: "\u2014 \u0627\u062E\u062A\u0631 \u2014" }), instanceIds.map((id) => (_jsx("option", { value: id, children: id }, id)))] })] })), needsFormula && (_jsxs("label", { className: "feedback-field", children: [_jsx("span", { children: "\u0627\u0644\u0635\u064A\u063A\u0629" }), _jsx("select", { value: step.formula, onChange: (e) => patchStep(step.uid, { formula: e.target.value }), children: SPECIES_FORMULAS.map((f) => (_jsx("option", { value: f, children: f }, f))) })] })), needsValue && (_jsxs("label", { className: "feedback-field builder-coord", children: [_jsx("span", { children: "\u0627\u0644\u0639\u062A\u0628\u0629" }), _jsx("input", { type: "number", step: "0.01", value: step.threshold, onChange: (e) => patchStep(step.uid, { threshold: e.target.value }) })] }))] }), _jsxs("label", { className: "feedback-field", children: [_jsx("span", { children: "\u0627\u0636\u0627\u0621\u0629 \u0647\u062F\u0641 (\u0627\u062E\u062A\u064A\u0627\u0631\u064A)" }), _jsx("input", { type: "text", value: step.highlight, onChange: (e) => patchStep(step.uid, { highlight: e.target.value }), placeholder: "\u0645\u0639\u0631\u0641 \u0627\u062F\u0627\u0629 \u0627\u0648 \u0645\u0641\u062A\u0627\u062D \u0632\u0631 \u0645\u062B\u0644 play / exit" })] })] }, step.uid));
                            }), _jsxs("div", { className: "builder-actions", children: [_jsx("button", { type: "button", className: "feedback-submit", onClick: onValidate, children: "\u062A\u062D\u0642\u0642" }), _jsx("button", { type: "button", className: "feedback-submit", onClick: onExport, children: "\u062A\u0635\u062F\u064A\u0631 JSON" }), _jsx("button", { type: "button", className: "feedback-submit", onClick: onImportClick, children: "\u0627\u0633\u062A\u064A\u0631\u0627\u062F JSON" }), _jsx("input", { ref: fileInputRef, type: "file", accept: "application/json,.json", style: { display: "none" }, onChange: onImport })] }), issues && (_jsxs("div", { className: "builder-issues", children: [_jsx("h3", { className: "builder-section-title", children: issues.length === 0 ? "لا توجد مشاكل." : `مشاكل (${issues.length})` }), issues.length > 0 && (_jsx("ul", { className: "builder-issue-list", children: issues.map((msg, i) => (_jsx("li", { children: msg }, i))) }))] }))] }), _jsxs("aside", { className: "builder-preview", children: [_jsx("h2", { className: "builder-section-title", children: "\u0645\u0639\u0627\u064A\u0646\u0629 JSON" }), _jsx("pre", { className: "builder-preview-pre", children: jsonText })] })] })] }));
}
